import React from 'react';
import { View, Text } from '@react-pdf/renderer';

import {
  AutofillAssessmentQuestion,
  ConditionalMultipleChoiceAssessmentQuestion,
  FileUploadAssessmentQuestion,
  MultiSelectAssessmentQuestion,
  MultiSignatureAssessmentQuestion,
  MultipleChoiceAssessmentQuestion,
  PatientContactAssessmentQuestion,
  SignatureUser,
} from '../../../../../generated/graphql';

import {
  isAutofillAssessmentQuestion,
  isConditionalMultipleChoiceAssessmentQuestion,
  isDatePickerAssessmentQuestion,
  isFileUploadAssessmentQuestion,
  isFreeTextAssessmentQuestion,
  isInitialsAssessmentQuestion,
  isMultiSelectAssessmentQuestion,
  isMultiSignatureAssessmentQuestion,
  isMultipleChoiceAssessmentQuestion,
  isPatientContactAssessmentQuestion,
  isProviderSignatureAssessmentQuestion,
  isRichTextAssessmentQuestion,
  isSignatureAssessmentQuestion,
  isStatementAssessmentQuestion,
} from '../../../../lib/assessments';
import { formatContactTypeLabel } from '../../../../lib/patientContactLabels';
import { SignatureUserTypeLabel } from '../../../../lib/multiSignature';
import {
  getAutofillSourceLabel,
  getQuestionText,
  isQuestionRequired,
  LibraryFormQuestion,
} from '../helpers';
import { blankFieldStyles, questionCardStyles } from '../styles';
import PdfFormattedText from './PdfFormattedText';

const BlankLine: React.FC<{ width?: string | number }> = ({
  width = '100%',
}) => <View style={[blankFieldStyles.line, { width }]} />;

const RuledLines: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <View style={blankFieldStyles.ruledLinesContainer}>
    {Array.from({ length: count }).map((_, index) => (
      <BlankLine key={index} />
    ))}
  </View>
);

const ChoiceOptions: React.FC<{
  options: string[];
  multiSelect?: boolean;
}> = ({ options, multiSelect = false }) => (
  <View style={blankFieldStyles.optionsContainer}>
    {options.map((option, index) => (
      <View key={index} style={blankFieldStyles.optionRow}>
        <View
          style={
            multiSelect
              ? blankFieldStyles.optionCheckboxMarker
              : blankFieldStyles.optionRadioMarker
          }
        />
        <View style={{ flex: 1 }}>
          <PdfFormattedText style={blankFieldStyles.optionText}>
            {option}
          </PdfFormattedText>
        </View>
      </View>
    ))}
  </View>
);

const SignatureBlock: React.FC<{ label: string }> = ({ label }) => (
  <View style={blankFieldStyles.signatureBlock}>
    <Text style={blankFieldStyles.signatureLabel}>{label}</Text>
    <BlankLine />
    <View style={blankFieldStyles.signatureMetaRow}>
      <Text style={blankFieldStyles.signatureMetaLabel}>Printed name:</Text>
      <View style={[blankFieldStyles.line, { flex: 1, marginLeft: 4 }]} />
    </View>
    <View style={blankFieldStyles.signatureMetaRow}>
      <Text style={blankFieldStyles.signatureMetaLabel}>Date:</Text>
      <View style={[blankFieldStyles.line, { flex: 1, marginLeft: 4 }]} />
    </View>
  </View>
);

const getSignatureRoleLabel = (signatureUserType: SignatureUser): string => {
  switch (signatureUserType) {
    case SignatureUser.PatientUser:
      return SignatureUserTypeLabel.PatientUser;
    case SignatureUser.SendingProviderUser:
      return SignatureUserTypeLabel.SendingProviderUser;
    case SignatureUser.AssignedProviderUser:
      return SignatureUserTypeLabel.AssignedProviderUser;
    default:
      return 'Signer';
  }
};

interface BlankQuestionFieldProps {
  question: LibraryFormQuestion;
  questionNumber?: number;
}

const BlankQuestionField: React.FC<BlankQuestionFieldProps> = ({
  question,
  questionNumber,
}) => {
  const questionText = getQuestionText(question);
  const showRequiredAsterisk =
    isQuestionRequired(question) && !isStatementAssessmentQuestion(question);
  const questionPrefix =
    questionNumber != null && !isStatementAssessmentQuestion(question)
      ? `${questionNumber}. `
      : '';

  if (isStatementAssessmentQuestion(question)) {
    return (
      <View style={{ marginBottom: 8 }}>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
          }}
        >
          {questionPrefix ? (
            <Text style={questionCardStyles.statementText}>
              {questionPrefix}
            </Text>
          ) : null}
          <View style={{ flex: 1 }}>
            <PdfFormattedText style={questionCardStyles.statementText}>
              {questionText}
            </PdfFormattedText>
          </View>
        </View>
      </View>
    );
  }

  const questionHeader = (
    <View style={questionCardStyles.questionHeader}>
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
        }}
      >
        {questionPrefix ? (
          <Text style={questionCardStyles.questionText}>{questionPrefix}</Text>
        ) : null}
        <View style={{ flex: 1 }}>
          <PdfFormattedText
            style={questionCardStyles.questionText}
            suffix={
              showRequiredAsterisk ? (
                <Text
                  style={[
                    questionCardStyles.questionText,
                    questionCardStyles.requiredAsterisk,
                  ]}
                >
                  {' *'}
                </Text>
              ) : undefined
            }
          >
            {questionText}
          </PdfFormattedText>
        </View>
      </View>
    </View>
  );

  let answerContent: React.ReactNode = null;

  if (isMultipleChoiceAssessmentQuestion(question)) {
    const mcQuestion = question as MultipleChoiceAssessmentQuestion;
    answerContent = (
      <ChoiceOptions
        options={mcQuestion.answerOptions?.map((o) => o.answerOption) ?? []}
      />
    );
  } else if (isMultiSelectAssessmentQuestion(question)) {
    const msQuestion = question as MultiSelectAssessmentQuestion;
    answerContent = (
      <ChoiceOptions
        options={msQuestion.answerOptions?.map((o) => o.answerOption) ?? []}
        multiSelect
      />
    );
  } else if (isConditionalMultipleChoiceAssessmentQuestion(question)) {
    const cmQuestion = question as ConditionalMultipleChoiceAssessmentQuestion;
    answerContent = (
      <View>
        <ChoiceOptions
          options={cmQuestion.answerOptions?.map((o) => o.answerOption) ?? []}
        />
        {cmQuestion.followUp?.question && (
          <>
            <View style={blankFieldStyles.followUpRow}>
              <Text style={blankFieldStyles.followUpLabel}>
                If applicable:{' '}
              </Text>
              <View style={{ flex: 1 }}>
                <PdfFormattedText style={blankFieldStyles.followUpLabel}>
                  {cmQuestion.followUp.question}
                </PdfFormattedText>
              </View>
            </View>
            <ChoiceOptions
              options={
                cmQuestion.followUp.answerOptions?.map((o) => o.answerOption) ??
                []
              }
            />
          </>
        )}
      </View>
    );
  } else if (isFreeTextAssessmentQuestion(question)) {
    answerContent = <BlankLine />;
  } else if (isRichTextAssessmentQuestion(question)) {
    answerContent = <RuledLines />;
  } else if (isInitialsAssessmentQuestion(question)) {
    answerContent = (
      <View style={blankFieldStyles.signatureBlock}>
        <View style={blankFieldStyles.signatureMetaRow}>
          <Text style={blankFieldStyles.signatureMetaLabel}>Initials:</Text>
          <View style={[blankFieldStyles.line, { flex: 1, marginLeft: 4 }]} />
        </View>
        <View style={blankFieldStyles.signatureMetaRow}>
          <Text style={blankFieldStyles.signatureMetaLabel}>Date:</Text>
          <View style={[blankFieldStyles.line, { flex: 1, marginLeft: 4 }]} />
        </View>
      </View>
    );
  } else if (
    isSignatureAssessmentQuestion(question) ||
    isProviderSignatureAssessmentQuestion(question)
  ) {
    const label = isProviderSignatureAssessmentQuestion(question)
      ? 'Practitioner signature'
      : 'Client signature';
    answerContent = <SignatureBlock label={label} />;
  } else if (isMultiSignatureAssessmentQuestion(question)) {
    const msQuestion = question as MultiSignatureAssessmentQuestion;
    answerContent = (
      <View>
        {msQuestion.assessmentSignatureUsers?.map((user, index) => (
          <SignatureBlock
            key={index}
            label={`${getSignatureRoleLabel(user.signatureUserType)} signature`}
          />
        ))}
      </View>
    );
  } else if (isDatePickerAssessmentQuestion(question)) {
    answerContent = (
      <View style={blankFieldStyles.signatureMetaRow}>
        <Text style={blankFieldStyles.signatureMetaLabel}>Date:</Text>
        <Text style={blankFieldStyles.datePlaceholder}>__ / __ / ____</Text>
      </View>
    );
  } else if (isAutofillAssessmentQuestion(question)) {
    const autofillQuestion = question as AutofillAssessmentQuestion;
    answerContent = (
      <View>
        <Text style={blankFieldStyles.fieldHint}>
          {getAutofillSourceLabel(autofillQuestion.autofillSource)}
        </Text>
        <BlankLine />
      </View>
    );
  } else if (isPatientContactAssessmentQuestion(question)) {
    const contactQuestion = question as PatientContactAssessmentQuestion;
    const contactLabel = formatContactTypeLabel(contactQuestion.contactType);
    answerContent = (
      <View style={blankFieldStyles.contactFields}>
        <Text style={blankFieldStyles.fieldHint}>{contactLabel}</Text>
        {['Name', 'Email', 'Phone'].map((label) => (
          <View key={label} style={blankFieldStyles.signatureMetaRow}>
            <Text style={blankFieldStyles.signatureMetaLabel}>{label}:</Text>
            <View style={[blankFieldStyles.line, { flex: 1, marginLeft: 4 }]} />
          </View>
        ))}
      </View>
    );
  } else if (isFileUploadAssessmentQuestion(question)) {
    const fileQuestion = question as FileUploadAssessmentQuestion;
    answerContent = (
      <Text style={blankFieldStyles.unsupportedNote}>
        File attachment — not applicable on paper copy
        {fileQuestion.allowedExtensions?.length
          ? ` (${fileQuestion.allowedExtensions.join(', ')})`
          : ''}
      </Text>
    );
  } else {
    answerContent = (
      <Text style={blankFieldStyles.unsupportedNote}>
        Unsupported on printable form
      </Text>
    );
  }

  return (
    <View wrap={false} style={questionCardStyles.card}>
      {questionHeader}
      {answerContent}
    </View>
  );
};

export default BlankQuestionField;
